// src/api.js
import axios from 'axios';

const user_backend = process.env.REACT_APP_USER_BACKEND_URL
const gym_backend = process.env.REACT_APP_GYM_BACKEND_URL


const api = axios.create({
  baseURL: gym_backend,
});

const getApiErrorMessage = (error) => {
  const data = error.response?.data;

  if (typeof data === 'string') return data;
  if (typeof data?.error === 'string') return data.error;
  if (typeof data?.detail === 'string') return data.detail;

  if (data && typeof data === 'object') {
    const firstValue = Object.values(data)[0];
    if (Array.isArray(firstValue) && firstValue.length > 0) {
      return String(firstValue[0]);
    }
  }

  return error.message || 'Une erreur inattendue est survenue.';
};

// Function to login
export const login = async (credentials) => {
  const response = await fetch(`${gym_backend}/login/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(credentials),
  });
  if (!response.ok) {
    throw new Error('Failed to login');
  }
  return await response.json();
};

// Function to refresh token
export const refreshToken = async (data) => {
  const response = await fetch(`${gym_backend}/token-refresh/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error('Failed to refresh token');
  }
  return await response.json();
};

// Function to add a gym
export const addGym = async (gymData) => {
  const formData = new FormData();

  Object.entries(gymData).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (Array.isArray(value)) {
      value.forEach((item) => formData.append(key, item));
      return;
    }

    if (typeof value === 'object' && !(value instanceof File)) {
      formData.append(key, JSON.stringify(value));
      return;
    }

    formData.append(key, value);
  });

  try {
    const response = await api.post('/gym/add-gym/', formData);
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

export const resendGymVerificationEmail = async (ownerEmail) => {
  try {
    const response = await api.post('/gym/resend-verification-email/', {
      owner_email: ownerEmail,
    });
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

export const verifyGymEmail = async (token) => {
  if (!token) {
    throw new Error('Le lien de confirmation est incomplet.');
  }

  try {
    const response = await api.get('/gym/verify-email/', {
      params: { token },
    });
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

// Function to list gyms
export const listGyms = async () => {
  return await api.get('/gym/list-gyms/');
};

// Function to get nearby gyms
export const getNearbyGyms = async (lat, lon, radius = 5) => {
  return await api.get(`/gym/nearby/?lat=${lat}&lon=${lon}&radius=${radius}`);
};

// Function to create a gym schedule
export const createSchedule = async (scheduleData, accessToken) => {
  const formData = new FormData();

  // Append form fields to formData
  for (const key in scheduleData) {
    if (scheduleData[key]) {
      formData.append(key, scheduleData[key]);
    }
  }

  try {
    const response = await api.post('/schedules/schedules/create/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': 'Bearer ' + String(accessToken),
      },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Function to get the user profile
export const getUserProfile = async (accessToken) => {
  const response = await fetch(`${gym_backend}/profile/profile/`, {
    method: 'GET',
    headers: {
      'Authorization': 'Bearer ' + accessToken,
    },
  });
  if (!response.ok) {
    throw new Error('Failed to fetch profile');
  }
  return await response.json();
};


// Function to get monthly schedules
export const getMonthlySchedules = async (month, sex, accessToken) => {
  const gymId = localStorage.getItem('gymId'); // Retrieve gymId from localStorage

  if (!gymId) {
    throw new Error('Gym ID not found in localStorage');
  }

  const response = await fetch(`${gym_backend}/schedules/schedules/get_monthly/?gym=${gymId}&month=${month}&sex=${sex}`, {
    method: 'GET',
    headers: {
      'Authorization': 'Bearer ' + accessToken,
    },
  });
  if (!response.ok) {
    throw new Error('Failed to fetch schedules');
  }
  return await response.json();
};

// Function to create a schedule range
export const createScheduleRange = async (data, accessToken) => {
  const gymId = localStorage.getItem('gymId'); // Retrieve gymId from localStorage

  if (!gymId) {
    throw new Error('Gym ID not found in localStorage');
  }

  const response = await fetch(`${gym_backend}/schedules/schedules/create_schedule_range/`, {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer ' + accessToken,
      'Content-Type': 'application/json', // Set the content type to JSON
    },
    body: JSON.stringify(data), // Convert the data object to JSON
  });

  if (!response.ok) {
    throw new Error('Failed to create schedule range');
  }
  return await response.json();
};

// Fetches the profile data for the authenticated user
export const fetchProfile = async (accessToken) => {
  try {
    const response = await fetch(`${gym_backend}/gym/details/`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('authTokens');
        window.location.href = '/auth';
      }
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Failed to fetch profile:', error);
    throw error;
  }
};

// Lit le statut commercial de la salle connectée.
export const getGymAvailability = async (accessToken) => {
  const response = await fetch(`${gym_backend}/gym/availability/`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    },
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.detail || 'Impossible de récupérer la disponibilité de la salle.');
  }
  return data;
};

// Ouvre ou suspend les nouvelles souscriptions sans désactiver le compte.
export const updateGymAvailability = async (active, accessToken) => {
  const response = await fetch(`${gym_backend}/gym/availability/`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ active: Boolean(active) }),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.detail || 'Impossible de modifier la disponibilité de la salle.');
  }
  return data;
};

// Fetches today's bookings with the given access token
export const fetchBookings = async (date, accessToken) => {
  const url = `${gym_backend}/booking/get_bookings/?date=${date}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Accept': 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error('Failed to fetch bookings');
  }
  return await response.json();
};


export const updateProfileImage = async (formData, accessToken) => {
  try {
    const response = await fetch(`${gym_backend}/medias/set_user_profile/`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'application/json', // Optional, in case you want to accept JSON response
      },
      body: formData, // FormData is used for multipart/form-data requests
    });

    if (!response.ok) {
      throw new Error('Failed to update profile image');
    }

    return await response.json(); // Parse the JSON response body
  } catch (error) {
    console.error('Failed to update profile image:', error);
    throw error;
  }
};


export const addGalleryImage = async (formData, accessToken) => {
  try {
    const response = await fetch(`${gym_backend}/medias/gallery/add-image/`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'application/json',
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to add image to gallery');
    }

    return await response.json();
  } catch (error) {
    console.error('Failed to add image to gallery:', error);
    throw error;
  }
};


export const removeGalleryImage = async (imageName, accessToken) => {
  try {
    const response = await fetch(`${gym_backend}/medias/gallery/remove_image/`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        image_name: imageName,  // Pass the image name as part of the request body
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to remove image from gallery');
    }

    return await response.json();
  } catch (error) {
    console.error('Failed to remove image from gallery:', error);
    throw error;
  }
};



export const updateGymlatlon = async (lat,lon, accessToken) => {
  try {
    const response = await fetch(`${gym_backend}/gym/update_gym/`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'accept': 'application/json',
      },
      body: JSON.stringify({ lat: lat,lon:lon }), // Data sent as JSON string
    });

    if (!response.ok) {
      throw new Error('Failed to update gym');
    }

    return await response.json(); // Return the response body as JSON
  } catch (error) {
    console.error('Failed to update gym:', error);
    throw error;
  }
};


export const updateGym = async (value, accessToken, field) => {
  try {
    const response = await fetch(`${gym_backend}/gym/update_gym/`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'accept': 'application/json',
      },
      body: JSON.stringify({ [field]: value }), // Data sent as JSON string
    });

    if (!response.ok) {
      throw new Error('Failed to update gym');
    }

    return await response.json(); // Return the response body as JSON
  } catch (error) {
    console.error('Failed to update gym:', error);
    throw error;
  }
};

const readFetchPayload = async (response) => {
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }
  const text = await response.text();
  return text ? { detail: text } : {};
};

const fetchErrorMessage = (payload, fallback) => {
  if (typeof payload?.detail === 'string') return payload.detail;
  if (typeof payload?.error === 'string') return payload.error;
  if (payload && typeof payload === 'object') {
    const value = Object.values(payload)[0];
    if (Array.isArray(value) && value.length) return String(value[0]);
    if (typeof value === 'string') return value;
  }
  return fallback;
};

const fieldErrorText = (value) => {
  if (Array.isArray(value)) return value.map(fieldErrorText).filter(Boolean).join(' ');
  if (value && typeof value === 'object') {
    return Object.values(value).map(fieldErrorText).filter(Boolean).join(' ');
  }
  return value == null ? '' : String(value);
};

export const updateGymProfile = async (payload, accessToken) => {
  const response = await fetch(`${gym_backend}/gym/update_gym/`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  const data = await readFetchPayload(response);
  if (!response.ok) {
    const requestError = new Error(fetchErrorMessage(data, 'Impossible de mettre à jour le profil.'));
    requestError.fieldErrors = data && typeof data === 'object'
      ? Object.fromEntries(
        Object.entries(data)
          .filter(([field]) => !['detail', 'error', 'non_field_errors'].includes(field))
          .map(([field, value]) => [field, fieldErrorText(value)]),
      )
      : {};
    throw requestError;
  }
  return data;
};

const mutateGymListItem = async (path, body, accessToken, method) => {
  const response = await fetch(`${gym_backend}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  const data = await readFetchPayload(response);
  if (!response.ok) {
    throw new Error(fetchErrorMessage(data, "L'opération n'a pas pu être effectuée."));
  }
  return data;
};

export const addGymFeature = (featureName, accessToken) => mutateGymListItem(
  '/features/add/',
  { feature_name: featureName },
  accessToken,
  'POST',
);

export const removeGymFeature = (featureName, accessToken) => mutateGymListItem(
  '/features/remove/',
  { feature_name: featureName },
  accessToken,
  'DELETE',
);

export const addGymEquipment = (equipmentName, accessToken) => mutateGymListItem(
  '/equipment/add/',
  { equipment_name: equipmentName },
  accessToken,
  'POST',
);

export const removeGymEquipment = (equipmentName, accessToken) => mutateGymListItem(
  '/equipment/remove/',
  { equipment_name: equipmentName },
  accessToken,
  'DELETE',
);


export const updateGymStep = async (value, otp_code, accessToken, field, step) => {
  try {
    const url = new URL(`${gym_backend}/gym/update_gym_step/`);
    url.searchParams.append('step', step); // Append step to query string

    // Prepare request body
    let requestBody = {};

    if (step === 2 && otp_code) {
      // For step 2, add OTP code to the request body
      requestBody.otp_code = otp_code;
    }

    // Handle other fields if necessary
    if (field && value !== null) {
      requestBody[field] = value;
    } else if (typeof value === 'object' && value !== null) {
      requestBody = { ...requestBody, ...value }; // For multi-field payloads like { otp_code, name, ... }
    }

    const response = await fetch(url.toString(), {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'accept': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    const data = await response.json();

    if (!response.ok) {
      console.error(`Step ${step} failed:`, data);
      throw new Error(`Step ${step} failed: ${data.detail || 'Unknown error'}`);
    }

    console.log(`Step ${step} successful:`, data);

    return {
      step,
      success: true,
      message: step === 1
          ? 'OTP code generated successfully.'
          : 'Gym updated successfully.',
      data,
    };
  } catch (error) {
    return {
      step,
      success: false,
      message: error.message || 'An error occurred during gym update.',
      error,
    };
  }
};




// Confirms booking using the provided verification code and check status
export const confirmBooking = async (code, checked, accessToken) => {
  const url = `${gym_backend}/booking/check_booking/?verification_code=${code}&checked=${checked}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Accept': 'application/json',
      // 'X-CSRFToken': 'YOUR_CSRF_TOKEN' WHY ARE YOU GETTING THIS PARAMETRE?
    }
  });

  if (!response.ok) {
    throw new Error('Failed to confirm booking');
  }
  return await response.json();
};

// Fetches profile picture by profile ID
export const fetchProfilePic = async (profileId) => {
  const url = `${gym_backend}/medias/profile/${profileId}`;
  const response = await fetch(url, { method: 'GET' });

  if (!response.ok) {
    throw new Error('Failed to fetch profile picture');
  }
  return await response.blob();
};

// Fetch profile picture
export const fetchProfilePicture = async (profilePath) => {
  try {
    const response = await fetch(`${gym_backend}/medias/profile/${profilePath}`, {
      method: 'GET',
    });
    if (response.ok) {
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } else {
      console.error('Failed to fetch profile picture:', response.statusText);
      return null;
    }
  } catch (error) {
    console.error('Error fetching profile picture:', error);
    return null;
  }
};

export const fetchNotificationCount = async (authTokens) => {
  const accessToken = authTokens?.access;
  if (!accessToken) return 0;

  try {
    const response = await fetch(`${gym_backend}/comments/unseen_count/`, {
      method: "GET",
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        "Accept": "application/json",
      },
    });
    if (response.ok) {
      const data = await response.json();
      return Number(data.unseen_comments) || 0;
    }
    return 0;
  } catch (_) {
    // Le badge de notification ne doit jamais bloquer la navigation.
    return 0;
  }
};

export const sendEmailVerification = async (ownerEmail) => {
  try {
    const response = await fetch(`${gym_backend}/gym/resend-verification-email/`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ owner_email: ownerEmail }),
    });

    const data = await response.json().catch(() => ({}));
    if (response.ok) return data;

    const message =
      (typeof data?.error === 'string' && data.error) ||
      (typeof data?.detail === 'string' && data.detail) ||
      "L'e-mail de vérification n'a pas pu être envoyé.";
    throw new Error(message);
  } catch (error) {
    console.error("Erreur lors de l'envoi de l'e-mail de vérification :", error);
    throw error;
  }
};

const membershipHeaders = (accessToken) => ({
  'Authorization': `Bearer ${accessToken}`,
  'Content-Type': 'application/json',
});

export const getMembershipPlans = async (accessToken) => {
  try {
    const response = await api.get('/schedules/pricing-plans/', {
      params: { plan_type: 'unlimited' },
      headers: membershipHeaders(accessToken),
    });
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

export const createMembershipPlan = async (plan, accessToken) => {
  try {
    const response = await api.post(
      '/schedules/pricing-plans/',
      { ...plan, plan_type: 'unlimited' },
      { headers: membershipHeaders(accessToken) },
    );
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

export const updateMembershipPlan = async (planId, plan, accessToken) => {
  try {
    const response = await api.patch(
      `/schedules/pricing-plans/${planId}/`,
      { ...plan, plan_type: 'unlimited' },
      { headers: membershipHeaders(accessToken) },
    );
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

export const getActiveGymMemberships = async (accessToken, search = '') => {
  try {
    const response = await api.get('/booking/memberships/active/', {
      params: search ? { search } : undefined,
      headers: membershipHeaders(accessToken),
    });
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

export const verifyGymMembershipCode = async (accessCode, accessToken) => {
  try {
    const response = await api.post(
      '/booking/memberships/verify/',
      { access_code: accessCode },
      { headers: membershipHeaders(accessToken) },
    );
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};


// Add more functions based on your API requirements...
